import { useRef, useState } from "react";
import { formatYear } from "@hunch/core";
import { useI18n } from "../../i18n/index.js";
import type { TimeslipCard } from "./api.js";
import styles from "./RevealList.module.css";

export interface RevealEntry {
  card: TimeslipCard;
  isGreen: boolean;
  truePosition: number;
  points: number;
}

interface RevealListProps {
  entries: RevealEntry[];
  correctOrder: TimeslipCard[];
  showCorrectOrderLabel: string;
  showMyOrderLabel: string;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function RevealList({ entries, correctOrder, showCorrectOrderLabel, showMyOrderLabel }: RevealListProps) {
  const { lang } = useI18n();
  const [order, setOrder] = useState<RevealEntry[]>(entries);
  const [showingCorrect, setShowingCorrect] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const nodeRefs = useRef(new Map<number, HTMLLIElement>());

  function setNodeRef(id: number, el: HTMLLIElement | null) {
    if (el) nodeRefs.current.set(id, el);
    else nodeRefs.current.delete(id);
  }

  function flipTo(nextOrder: RevealEntry[]) {
    const before = new Map<number, number>();
    for (const [id, el] of nodeRefs.current) before.set(id, el.getBoundingClientRect().top);

    setOrder(nextOrder);

    requestAnimationFrame(() => {
      const reduced = prefersReducedMotion();
      for (const [id, el] of nodeRefs.current) {
        const prevTop = before.get(id);
        if (prevTop === undefined) continue;
        const newTop = el.getBoundingClientRect().top;
        const delta = prevTop - newTop;
        if (delta === 0) continue;
        el.style.transition = "none";
        el.style.transform = `translateY(${delta}px)`;
        // Force reflow so the next style change actually transitions.
        void el.offsetHeight;
        el.style.transition = reduced ? "none" : "transform 0.4s ease";
        el.style.transform = "translateY(0)";
      }
    });
  }

  // Persistent toggle, not a transient animation: switches between the
  // player's submitted order and the correct order, and stays there until
  // toggled back — the button label reflects which one is currently shown.
  function handleToggleOrder() {
    if (showingCorrect) {
      flipTo(entries);
      setShowingCorrect(false);
      return;
    }
    const correctEntryOrder = correctOrder.map(
      (c) => order.find((e) => e.card.id === c.id) ?? entries.find((e) => e.card.id === c.id)!,
    );
    flipTo(correctEntryOrder);
    setShowingCorrect(true);
  }

  return (
    <div>
      <ol className={styles.list}>
        {order.map((entry) => (
          <li
            key={entry.card.id}
            ref={(el) => setNodeRef(entry.card.id, el)}
            className={`${styles.card} ${entry.isGreen ? styles.correct : styles.incorrect}`}
          >
            <div className={styles.row}>
              <div className={styles.iconColumn}>
                <span className={styles.icon} aria-hidden>
                  {entry.isGreen ? "✓" : "✗"}
                </span>
                <InfoButton
                  expanded={expandedId === entry.card.id}
                  onToggle={() => setExpandedId((cur) => (cur === entry.card.id ? null : entry.card.id))}
                />
              </div>
              <span className={styles.text}>{lang === "de" ? entry.card.textDe : entry.card.textEn}</span>
              <span className={styles.year}>{formatYear(entry.card.year, lang)}</span>
            </div>
            {entry.isGreen && <div className={styles.pointsBadge}>+{entry.points}</div>}
            {expandedId === entry.card.id && <CardDetails card={entry.card} />}
          </li>
        ))}
      </ol>
      <button type="button" className={styles.showOrderButton} onClick={handleToggleOrder}>
        {showingCorrect ? showMyOrderLabel : showCorrectOrderLabel}
      </button>
    </div>
  );
}

function InfoButton({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      className={styles.infoButton}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={t("timeslip.play.more")}
    >
      <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden focusable="false">
        <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="10" cy="6.3" r="1.15" fill="currentColor" />
        <path d="M10 9.2 V14.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function CardDetails({ card }: { card: TimeslipCard }) {
  const { lang, t } = useI18n();
  const info = lang === "de" ? card.infoDe : card.infoEn;

  return (
    <div className={styles.detailsBody}>
      {info && <p>{info}</p>}
      <a href={card.sourceUrl} target="_blank" rel="noopener noreferrer">
        {t("timeslip.play.source")}: {card.sourceLabel}
      </a>
    </div>
  );
}
